import type { RouterOutputs } from "@repo/api";
import { formatIntToCurrency } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { ColumnDefWithTotal } from "~/types";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { api } from "~/lib/api";
import { useReportStore } from "~/stores/use-report-store";

export const Route = createFileRoute("/_main/reports/_layout/employee")({
  component: () => <EmployeeReports />,
});

type Report = RouterOutputs["report"]["getEmployeeReports"][0];

function EmployeeReports() {
  const { range } = useReportStore();

  const { t } = useTranslation("common");

  const { data: reportData } = api.report.getEmployeeReports.useQuery({
    start: range.from,
    end: range.to,
  });

  const columns: ColumnDefWithTotal<Report>[] = [
    {
      id: t("Report.Employee.name"),
      accessorKey: "employeeName",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.employee_name")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.employeeName}</p>;
      },
    },
    {
      id: t("Report.Employee.items_sold"),
      accessorKey: "itemsSold",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.Employee.items_sold")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.itemsSold}</p>;
      },
    },
    {
      id: t("Report.Employee.revenue_created"),
      accessorKey: "revenueCreated",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.Employee.revenue_created")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{formatIntToCurrency(report.revenueCreated, false)}</p>;
      },
    },
    {
      id: t("Report.Employee.tips"),
      accessorKey: "tips",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.Employee.tips")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{formatIntToCurrency(report.tips, false)}</p>;
      },
    },
    {
      id: t("Report.Employee.receipts"),
      accessorKey: "receipts",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.Employee.receipts")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.receipts}</p>;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        defaultSorting={[{ id: t("Report.Employee.name"), desc: false }]}
        pdfExport={{
          filename: "employee-report",
        }}
        csvExport={{
          filename: "employee-report",
        }}
        columns={columns}
        data={reportData ?? []}
      />
    </div>
  );
}
